import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Box, Button, Flex, Text, Image } from 'rebass';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import store from 'store';
import { IoIosArrowBack } from "react-icons/io";
import useEntity from 'entities/hooks/use-entity';
import { FaArrowRight } from 'react-icons/fa';
import { BsBookHalf } from 'react-icons/bs';
import { BiBookContent, BiBookHeart } from 'react-icons/bi';
import Tippy from '@tippy.js/react';
import useHistory from 'app/hooks/history';
import useLocation from 'app/hooks/location';
import BabyOwlBookTransparent from './assets/BabyOwlBookTransparent.png';
import BabyBunnyBookTransparent from './assets/BabybunnyBook_Transparent.png';
import BabyOwlBookColored from './assets/BabyOwlBookColored.png';
import EditBookDetails from './BookDetails';
import OrganizeStories from './OrganizeStories';
import useEntities from 'entities/hooks/use-entities';
import OrganizeChapters from './OrganizeChapters';
import EditBook from './EditBook';
import EllipsisIcon from 'assets/svg/ellipsis-icon';
import { useWindowSize } from 'react-use';
import { get, getSWR, patch } from 'utils/api';
import useEntityActionCreators from 'entities/hooks/use-entity-action-creators';
import useToggle from 'app/hooks/toggle';
import useOutsideClick from 'use-onclickoutside';
import selectEntity from 'entities/selectors';
import { useDispatch, useSelector } from 'react-redux';
import Banner from 'app/payment/Banner';
import { transformAllPages } from './helper';
import { PageProvider } from './PageContext';
import BufferingOverlay from 'app/modal/BufferingOverlay';
import { useMobileCheckHook } from 'story-media/hooks';
import { setCheckmark } from 'ui/actions';
import usePageLayerEditor from './hooks/usePageLayerEditor';
import useUnsavedChangesGuard from './hooks/useUnsavedChangesGuard';
import UnsavedChangesModal from './components/UnsavedChangesModal';

const useCheckIfCoverPhotoExist = (frontCoverEntity) => {
  const layers = frontCoverEntity?.['front-cover-layers'] || [];

  const hasValidPhoto = useSelector((state) => {
    return layers.some(({ id, type }) => {
      if (!id || !type) return false;
      const entity = selectEntity({ state, id, type });
      if (entity?.['component-value'] !== 'photo') return false;
      const photoId = entity?.photo?.id;
      if (!photoId) return false;
      const image = selectEntity({ state, id: photoId, type: 'images' });
      return image && Object.keys(image).length > 0;
    });
  });

  return hasValidPhoto;
};

function organizeStoriesByChapter(response, chaptersMap) {
  const data = response.data;
  const included = response.included;

  const storiesMap = new Map();
  const bookStoriesMap = new Map();

  included.forEach(item => {
    if (item.type === 'stories') storiesMap.set(item.id, item);
  });

  data.forEach(item => {
    if (item.type === 'book-story') {
      const storyId = item.relationships.story.data.id;
      bookStoriesMap.set(storyId, item);
    }
  });

  const chaptersInResponse = included.filter(item => item.type === 'chapter');
  const rawChapterMap = {};
  const storiesWithChapters = new Set();

  chaptersInResponse.forEach(chapter => {
    const chapterId = chapter.id;
    const storyRefs = chapter.relationships.stories.data;

    if (!rawChapterMap[chapterId]) {
      rawChapterMap[chapterId] = {
        id: chapterId,
        name: chapter.attributes.name,
        kind: chapter.attributes.kind,
        state: chapter.attributes.state,
        position: chaptersMap[chapterId]?.position ?? 999,
        stories: [],
      };
    }

    storyRefs.forEach(storyRef => {
      const storyId = storyRef.id;
      const story = storiesMap.get(storyId);
      const bookStory = bookStoriesMap.get(storyId);
      if (!story || !bookStory) return;

      storiesWithChapters.add(storyId);
      rawChapterMap[chapterId].stories.push({
        id: bookStory.attributes.id,
        chapterId,
        storyId,
        position: bookStory.attributes.position,
        name: story.attributes.title,
        createdAt: story.attributes['began-on'],
        state: bookStory.attributes.state,
        coverPhoto: story.attributes['image-url'],
      });
    });
  });

  const noChapterKey = 'noChapter';
  rawChapterMap[noChapterKey] = {
    id: noChapterKey,
    name: 'Stories that need a Chapter',
    position: -1,
    stories: [],
  };

  storiesMap.forEach((story, storyId) => {
    if (!storiesWithChapters.has(storyId)) {
      const bookStory = bookStoriesMap.get(storyId);
      if (!bookStory) return;
      rawChapterMap[noChapterKey].stories.push({
        id: bookStory.attributes.id,
        chapterId: null,
        storyId,
        position: bookStory.attributes.position,
        name: story.attributes.title,
        createdAt: story.attributes['began-on'],
        state: bookStory.attributes.state,
        coverPhoto: story.attributes['image-url'],
      });
    }
  });

  Object.values(chaptersMap).forEach(chapter => {
    if (!rawChapterMap[chapter.id]) {
      rawChapterMap[chapter.id] = {
        id: chapter.id,
        name: chapter.name,
        position: chapter.position,
        kind: chapter.kind,
        state: chapter.state,
        stories: [],
      };
    }
  });

  return Object.values(rawChapterMap).sort((a, b) => {
    if (a.id === noChapterKey) return -1;
    if (b.id === noChapterKey) return 1;
    return a.position - b.position;
  });
}

const Header = ({
  step,
  setStep,
  title,
  revalidateBook,
  setIsValidating,
  subtitle,
  onBack,
  backText = 'Back',
  onNextPress,
  nextButtonText = 'Next >',
  firstBookId,
  showTooltip,
}) => {
  const [isTooltipOpen, setTooltipOpen] = useToggle();
  const [generatingBook, setGeneratingBook] = useToggle();
  const wrapper = useRef(null);
  const dispatch = useDispatch();

  useEffect(() => {
    if (generatingBook)
      setGeneratingBook(false);
  }, [isTooltipOpen]);

  useOutsideClick(wrapper, () => {
    if (isTooltipOpen) {

      setTooltipOpen();
    }
  });


  if (showTooltip) {
    return <Tippy
      content={
        <Box sx={{ width: '215.445px', flexShrink: 0 }} ref={wrapper}>
          <Flex
            sx={{ cursor: 'pointer' }}
            p="12px 16px"
            justifyContent="flex-start"
            alignItems="center"
            onClick={() => setStep(1)}
          >
            <BiBookHeart size={24} color={step === 1 ? '#000' : '#64748B'} />
            <Text css={`color: ${step === 1 ? '#000' : '#64748B'}; font-feature-settings: 'liga' off, 'clig' off; margin-left: 5px; font-family: Poppins; font-size: 16px; font-style: normal; font-weight: 500; line-height: 20px;`}>
              Edit Cover
            </Text>
          </Flex>
          <Flex
            sx={{ cursor: 'pointer' }}
            p="12px 16px"
            justifyContent="flex-start"
            alignItems="center"
            onClick={() => setStep(2)}
          >
            <BiBookContent size={24} color={step === 2 ? '#000' : '#64748B'} />
            <Text css={`color: ${step === 2 ? '#000' : '#64748B'}; font-feature-settings: 'liga' off, 'clig' off; margin-left: 5px; font-family: Poppins; font-size: 16px; font-style: normal; font-weight: 500; line-height: 20px;`}>
              Organize Stories
            </Text>
          </Flex>
          <Flex
            sx={{ cursor: 'pointer' }}
            p="12px 16px"
            justifyContent="flex-start"
            alignItems="center"
            onClick={() => setStep(3)}
          >
            <BsBookHalf size={24} color={step === 3 ? '#000' : '#64748B'} />
            <Text css={`color: ${step === 3 ? '#000' : '#64748B'}; font-feature-settings: 'liga' off, 'clig' off; margin-left: 5px; font-family: Poppins; font-size: 16px; font-style: normal; font-weight: 500; line-height: 20px;`}>
              Organize Chapters
            </Text>
          </Flex>
          <Flex
            disabled={generatingBook}
            sx={{ cursor: generatingBook ? 'not-allowed' : 'pointer' }}
            p="12px 16px"
            justifyContent="flex-start"
            alignItems="center"
            onClick={
              generatingBook
                ? undefined
                : async () => {
                  setGeneratingBook();
                  await get(`/v3/books/${firstBookId}.pdf`);
                  if (generatingBook)
                    setGeneratingBook(false);
                  setTooltipOpen(false);
                  dispatch(setCheckmark({
                    message: 'Your PDF is being generated. You’ll receive it by email as soon as it’s ready.',
                    top: 100,
                    height: '300px',
                    width: '300px'
                  }));

                  revalidateBook();
                }
            }
          >
            <BiBookHeart size={24} color={'#64748B'} />
            <Text className={generatingBook ? 'blacktextloading' : ''} css={`color: #64748B; font-feature-settings: 'liga' off, 'clig' off; margin-left: 5px; font-family: Poppins; font-size: 16px; font-style: normal; font-weight: 500; line-height: 20px;`}>
              {generatingBook ? 'Processing' : 'Generate PDF'}
            </Text>
          </Flex>
        </Box>
      }
      interactive
      theme="light"
      arrow
      trigger="manual"
      placement="bottom-end"
      zIndex="10000"
      visible={isTooltipOpen}
    >
      <Button
        width={40}
        onClick={setTooltipOpen}
        css={`
    height: 40px;
    border-radius: 5px;
    background: transparent;
    border: 0px solid #34495E;
    box-shadow: 0 0 0px 0 rgba(155, 89, 182, 0.15) inset;
    display: flex;
    padding: 4px;
    align-items: center;
    justify-content: center;
    gap: 10px;
  `}
      >
        <EllipsisIcon />
      </Button>
    </Tippy>
  }

  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      gap="10px"
      sx={{
        top: 0,
        width: '100%',
        bg: 'white',
        px: 3,
        py: 2,
        // pl: [2, '30px'],
        zIndex: 4,
        mt: ['60px', '60px', '60px', 0],
      }}
    >
      <Flex>
        {onBack && (
          <Flex
            justifyContent="space-around"
            alignItems="center"
            bg="#fff"
            mr={2}
            sx={{ borderRadius: 'medium', cursor: 'pointer' }}
            onClick={onBack}
          >
            <Text as={IoIosArrowBack} color="mineShaft" size={24} />
            <Text
              display={['none', 'block']}
              minWidth={'90px'}
              color="mineShaft"
              fontFamily="Poppins"
              lineHeight={'150%'}
              ml={['4px', 1]}
              fontWeight={400}
              fontSize="15px"
            >
              {backText}
            </Text>
          </Flex>
        )}
        <Box>
          <Text
            fontFamily="Poppins"
            fontSize={14}
            letterSpacing="0.22px"
            lineHeight="normal"
            color="#000"
            fontWeight={500}
          >
            {title}
          </Text>
          <Text
            fontFamily="Poppins"
            fontSize={14}
            letterSpacing="0.22px"
            lineHeight="normal"
            color="#64748B"
            fontWeight={400}
          >
            {subtitle}
          </Text>
        </Box>
      </Flex>


      {onNextPress && <Button
        onClick={onNextPress}
        sx={{
          borderRadius: 'medium',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {nextButtonText}
        {/* <Box ml={2} as={FaArrowRight} color="#fff" /> */}
      </Button>}
    </Flex>
  );
};

const BookCreationForm = () => {
  const params = useParams();
  const { entity } = useEntity(params);
  useSWR(`/v3/child_journals/${params.id}`);

  const firstBookId = entity?.books?.[0]?.id || '';
  const { push, replace } = useHistory();
  const { search, pathname } = useLocation();

  const { entity: bookCollection } = useEntity({ type: 'book', id: firstBookId });
  const { entity: frontCoverEntity } = useEntity({
    id: bookCollection?.['front-cover']?.id,
    type: 'front-covers',
  });

  const [ftue, setFtue] = useState(false);
  const [step, setStep] = useState(null);
  const [isDefaultStepSet, setIsDefaultStepSet] = useState(false);
  const { width } = useWindowSize();

  const [missingCoverPhotoError, setMissingCoverPhotoError] = useState(null);

  // ✅ FIX: isValidating starts true — spinner shows until server confirms
  //    'finished_creating_pages'. It is only ever set to false by the
  //    bookData effect below (the single source of truth).
  const [isValidating, setIsValidating] = useState(false);
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(null);

  const [selectedPageIndex, setSelectedPageIndex] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPagesData, setTotalPagesData] = useState([]);

  // Client-side move/resize/undo-redo/save session for whichever page is
  // currently being edited. Lifted up here (rather than inside PageSlider)
  // so both the thumbnail sidebar (EditBook's PageComponent) and the main
  // flipbook can be guarded by the same unsaved-changes check when the
  // member tries to navigate to a different page.
  //
  // isLocked is intentionally left unset: the authoritative lock state for
  // whichever page(s) are on screen lives in PageSlider's own usePageLock
  // instances (the same ones driving the lock-toggle UI), and the
  // selection/drag UI (MediaSelectionOverlay) already refuses to call
  // selectLayer/beginInteraction at all when its page is locked. Duplicating
  // that lock state here would just create a second, possibly-stale source
  // of truth for the same thing.
  const layerEditor = usePageLayerEditor({ firstBookId, currentPage });

  const navigationGuard = useUnsavedChangesGuard({
    hasUnsavedChanges: layerEditor.hasUnsavedChanges,
    onSave: layerEditor.save,
    onDiscard: layerEditor.discard,
  });

  // The "save before moving on?" modal only covers leaving the page via a
  // real URL change or closing/refreshing the tab (see navigationGuard's
  // <Prompt> + beforeunload below) — switching pages/steps in-app is left
  // alone and never prompts.
  const pageContextValue = useMemo(() => ({
    currentPage,
    setCurrentPage,
    selectedPageIndex,
    setSelectedPageIndex,
    totalPagesData,
    setTotalPagesData,
    loading,
    layerEditor,
  }), [currentPage, selectedPageIndex, totalPagesData, loading, layerEditor]);

  // ✅ FIX: Use 0 (not null) as the initial/disabled value for SWR refreshInterval.
  //    SWR treats 0 as "no polling". null causes undefined behaviour.
  const [refreshInterval, setRefreshInterval] = useState(0);

  const { revalidate: revalidateBook, data: bookData } = useSWR(
    firstBookId ? `/v3/books/${firstBookId}` : null,
    getSWR,
    { refreshInterval }
  );

  // Derived directly from bookData — no intermediate state, never stale
  const stateForPageGeneration = bookData?.data?.data?.attributes?.['state-for-page-generation'];

  const totalPages = bookCollection?.['pages-count'];
  const [refreshKey, setRefreshKey] = useState(Math.random());
  const { receiveDeletion } = useEntityActionCreators();

  const shouldFetch =
    firstBookId &&
    step === 4 &&
    bookData?.data?.data?.attributes?.['state-for-page-generation'] === 'finished_creating_pages' &&
    !isValidating &&                              // only fetch pages once generation done
    totalPagesData.length !== totalPages;

  const { data: pagesData, revalidate: revalidatePages } = useSWR(
    shouldFetch ? [`/v3/books/${firstBookId}/pages?page=${page}&per=10`, refreshKey] : null
  );

  const pagesDataItems = pagesData?.data ? transformAllPages(pagesData?.data) : pagesData?.data?.data;

  useEffect(() => {
    if (!pagesDataItems || pagesDataItems.length === 0) return;

    setPage(prev => prev + 1);
    setTotalPagesData(prev => [...prev, ...pagesDataItems]);
  }, [pagesDataItems]);

  // ─── Detect when all pages have loaded ─────────────────────────────────────
  useEffect(() => {
    if (totalPages && totalPagesData.length >= totalPages) {
      setLoading(false);
    }
  }, [totalPagesData.length, totalPages]);

  const isCoverPhotoExist = useCheckIfCoverPhotoExist(frontCoverEntity);

  // Single source of truth for spinner + polling.
  // Depends on bookData (new object reference on every SWR fetch) so it fires
  // reliably on every response — the derived string dep was silently skipped
  // when the value arrived in the same render it was already resolved.
  // A ref gates out no-op calls when the value didn't actually change.
  const prevStateForPageGenerationRef = useRef(null);
  useEffect(() => {

    if (!stateForPageGeneration) return;
    if (stateForPageGeneration === prevStateForPageGenerationRef.current) return;
    prevStateForPageGenerationRef.current = stateForPageGeneration;

    if (stateForPageGeneration !== 'finished_creating_pages') {
      setIsValidating(true);
      setRefreshInterval(2000);
    } else {
      setIsValidating(false);
      setRefreshInterval(0);
    }
  }, [bookData]); // eslint-disable-line

  // ─── generatePage ───────────────────────────────────────────────────────────
  // ✅ FIX: Do NOT call setIsValidating(false) here.
  //    The effect above is the single authority on when generation completes.
  //    Setting it false prematurely caused pages to start fetching before
  //    the server had actually finished generating them.
  const generatePage = useCallback(async () => {
    receiveDeletion({ entityType: 'book-page-layers' });
    receiveDeletion({ entityType: 'book-pages' });
    receiveDeletion({ entityType: 'book-story' });
    setIsValidating(true);

    prevStateForPageGenerationRef.current = 'creating_pages';
    setRefreshInterval(2000); // restart polling immediately

    await patch(`/v3/books/${firstBookId}/generate`);
    revalidateBook()

    receiveDeletion({ entityType: 'book-page-layers' });
    receiveDeletion({ entityType: 'book-pages' });
    receiveDeletion({ entityType: 'book-story' });
    // Reset page accumulator so fresh pages load after generation
    setTotalPagesData([]);
    setPage(1);
    // setLoading(true);
    setRefreshKey(prev => prev + 1);
  }, [firstBookId, receiveDeletion]);

  useEffect(() => {

    if (bookCollection?.['pages-changed']) {
      generatePage();
    }
  }, [bookCollection?.['pages-changed']]);

  // useEffect(() => {
  //   if (!isDefaultStepSet) {
  //     if (isCoverPhotoExist) {
  //       setStep(4)
  //       setMissingCoverPhotoError(false);
  //     }
  //     else setStep(1);
  //   }
  // }, [isCoverPhotoExist]);

  function getStepContent(appender = '?') {
    const queryParams = new URLSearchParams(search);
    const ref = queryParams.get('ref');
    const step = queryParams.get('step');

    const pageIndex = queryParams.get('pageIndex');
    if (pageIndex) {
      return `${appender}pageIndex=${pageIndex}`;
    } else if (ref) {
      return `${appender}ref=${ref}`;
    } else if (step && appender === '?') {
      return `${appender}step=${step}`;
    }
    return '';

  }

  useEffect(() => {

    if (step) {
      const url =
        `/${params.type}/${params.id}/book-creation?step=${step}` +
        getStepContent('&');
      push(url);
    } else {
      const url =
        `/${params.type}/${params.id}/book-creation` + getStepContent();
      push(url);
    }
    revalidateBook()
  }, [step]);

  const pages = useEntities('book-page-layers');
  const bookPageLayers = Object.values(pages);

  useEffect(() => {
    const currentPageIndex = totalPagesData?.findIndex(
      p => p?.id === currentPage?.id
    );
    setSelectedPageIndex(currentPageIndex !== -1 ? currentPageIndex : 0);
  }, [currentPage?.id]);

  useEffect(() => {
    if (totalPagesData?.length > 0) {
      if (!currentPage) setCurrentPage(totalPagesData[0]);
    } else {
      setCurrentPage(null);
    }
  }, [totalPagesData.length]);

  useEffect(() => {
    const queryParams = new URLSearchParams(search);
    const ref = queryParams.get('ref');
    const pageIndex = queryParams.get('pageIndex');

    if ((ref || pageIndex) && totalPagesData?.length > 0 && bookPageLayers.length > 0) {

      if (pageIndex) {
        const pageToSelect = totalPagesData[parseInt(pageIndex, 10)];

        if (pageToSelect) {
          setCurrentPage(pageToSelect);
          setSelectedPageIndex(parseInt(pageIndex, 10));
          queryParams.delete('pageIndex');

          setTimeout(
            () => replace({ pathname, search: 'step=4' }),
            500
          );
        }
      }
      else {
        const findPageLayer = bookPageLayers.find(
          layer => layer?.['source-media-id'] === ref
        );
        const currentPageIndex = totalPagesData.findIndex(({ layers }) => {
          return layers?.find(({ children }) => children.find(({
            id, 'source-media-id': sourceMediaId }) => (id === findPageLayer?.id || (sourceMediaId === findPageLayer?.['source-media-id']))))
        });
        const pageToSelect = totalPagesData[currentPageIndex];

        if (pageToSelect) {
          setCurrentPage(pageToSelect);
          setSelectedPageIndex(currentPageIndex);
          queryParams.delete('ref');

          setTimeout(
            () => replace({ pathname, search: 'step=4' }),
            500
          );
        }
      }
    }
  }, [totalPagesData.length, bookPageLayers.length]);

  const { data: front_cover_templates_data } =
    useSWR(firstBookId ? `/v3/books/${firstBookId}/front_cover_templates` : null);

  const frontCoverTemplates = front_cover_templates_data?.data?.data;

  const { revalidate, data: chaptersData } = useSWR(
    params.id ? `/v3/journals/${params.id}/chapters?system=true` : null
  );
  const { data, revalidate: revalidateBookStories } = useSWR(
    firstBookId ? `/v3/books/${firstBookId}/book_stories` : null
  );
  const chapters = useEntities('chapter');
  const [structuredStories, setStructuredStories] = useState([])

  useEffect(() => {
    setStructuredStories(organizeStoriesByChapter(
      data?.data || { data: [], included: [] },
      chapters
    ))
  }, [data?.data, chapters]);

  // ─── Set initial step from URL or book state ────────────────────────────────
  // ✅ FIX: Consolidated into a single effect to avoid two effects racing to
  //    set step. The URL param takes precedence; falls back to book state.
  useEffect(() => {
    const queryParams = new URLSearchParams(search);
    const stepFromUrl = queryParams.get('step');
    if (stepFromUrl) {
      setStep(parseInt(stepFromUrl, 10));
    }
  }, [search]);

  useEffect(() => {
    if (!store.get('book_creation_ftui_watched')) setFtue(true);
  }, []);
  const isFromMobile = useMobileCheckHook();

  if (width < 992 && step === 4) {
    return (
      <Flex
        justifyContent="center"
        flexDirection="column"
        alignItems="center"
        height="100vh"
        px={3}
      >
        <Text
          fontSize={24}
          fontFamily="Bitter"
          lineHeight="120%"
          letterSpacing="0.18px"
          color="#0E0E0E"
          textAlign="center"
        >
          Our book module only works on a large screen.
          <br />
          <br />
          {isFromMobile ? 'Please try it out on a desktop computer' : 'Please try expanding your window'}.
        </Text>
        <Image maxWidth={253} src={BabyBunnyBookTransparent} alt="No stories yet" />
      </Flex>
    );
  }

  // ─── Early returns (no hooks after this point) ──────────────────────────────
  if (entity?.['story-count'] === 0) {
    return (
      <Flex
        justifyContent="center"
        flexDirection="column"
        alignItems="center"
        height="100vh"
      >
        <Text
          fontSize={30}
          fontFamily="Bitter"
          lineHeight="120%"
          letterSpacing="0.18px"
          color="#0E0E0E"
          textAlign="center"
        >
          Add at least one story to begin building your book
        </Text>
        <Image maxWidth={253} src={BabyOwlBookTransparent} alt="No stories yet" />
        <Button
          color="white"
          fontSize={18}
          fontFamily="Poppins"
          lineHeight="150%"
          letterSpacing="0.32px"
          px={4}
          py={3}
          mt={3}
          onClick={() => push(`/${params.type}/${params.id}`)}
        >
          To Stories Dashboard
        </Button>
      </Flex>
    );
  }

  if (entity?.['story-count'] > 0 && ftue) {
    return (
      <Flex
        justifyContent="center"
        flexDirection="column"
        alignItems="center"
        height="100vh"
      >
        <Text
          fontSize={30}
          fontFamily="Bitter"
          lineHeight="120%"
          letterSpacing="0.18px"
          color="#0E0E0E"
          textAlign="center"
        >
          Let’s create your book layout<br /> from your stories.
        </Text>
        <Image maxWidth={253} src={BabyOwlBookColored} alt="No stories yet" />
        <Button
          color="white"
          fontSize={18}
          fontFamily="Poppins"
          lineHeight="150%"
          letterSpacing="0.32px"
          px={4}
          py={3}
          mt={3}
          onClick={() => {
            store.set('book_creation_ftui_watched', true);
            setFtue(false);
          }}
        >
          Get Started!
        </Button>
      </Flex>
    );
  }

  // ─── Step renderers ─────────────────────────────────────────────────────────
  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <EditBookDetails
            babysFullName={entity?.['first-name-possessive']}
            revalidateBook={revalidateBook}
            firstBookId={firstBookId}
            frontCoverTemplates={frontCoverTemplates || []}
            setIsDefaultStepSet={setIsDefaultStepSet}
          />
        );
      case 2:
        return (
          <OrganizeStories
            firstBookId={firstBookId}
            structuredStories={structuredStories}
          />
        );
      case 3:
        return (
          <OrganizeChapters
            chapters={chapters}
            revalidate={() => {
              revalidateBookStories();
              revalidate();
            }}
            firstBookId={firstBookId}
            structuredStories={structuredStories}
          />
        );
      case 4:
        return (
          <PageProvider value={pageContextValue}>
            {navigationGuard.promptElement}
            {navigationGuard.isOpen && (
              <UnsavedChangesModal
                isSaving={navigationGuard.isSaving}
                error={navigationGuard.error}
                onSave={navigationGuard.confirmSave}
                onDiscard={navigationGuard.confirmDiscard}
                onCancel={navigationGuard.cancel}
              />
            )}
            <BufferingOverlay color="#9B59B6" bg="rgba(255, 255, 255, 0.9)" isBuffering={isValidating || prevStateForPageGenerationRef?.current !== 'finished_creating_pages'} sx={{
              height: 'calc(100vh - 88px)'
            }} showLoadingText loadingText="Generating pages">
              <EditBook
                revalidate={() => {
                  revalidate();
                  revalidatePages();
                }}

                firstBookId={firstBookId}
                HeaderComponent={<Header showTooltip setStep={setStep} firstBookId={firstBookId} revalidateBook={revalidateBook} />}
              />
            </BufferingOverlay>
          </PageProvider >
        );
      default:
        return null;
    }
  };

  const getSubTitle = () => {
    switch (step) {
      case 1: return 'Book Cover';
      case 2: return 'Organize Stories';
      case 3: return 'Organize Chapters';
      case 4:
        return (
          <>
            {selectedPageIndex}{selectedPageIndex && '/'}{totalPagesData?.length} Pages{' '}
            <Text as="span" color="#F2949C">Min. 20 to print</Text>
          </>
        );
      default: return null;
    }
  };

  const getTitle = () => {
    switch (step) {
      case 1: return 'Start My Book 1/3';
      case 2: return 'Start My Book 2/3';
      case 3: return 'Start My Book 3/3';
      case 4: return `${bookCollection?.title} First Year`;
      default: return null;
    }
  };

  return (
    <Flex justifyContent="center" flexDirection="column" alignItems="center">
      <Header
        step={step}
        revalidateBook={revalidateBook}
        setIsValidating={setIsValidating}
        setStep={setStep}
        title={getTitle()}
        subtitle={getSubTitle()}
        nextButtonText={step === 1 ? 'Next step >' : undefined}
        onBack={
          step === 1 || step === 4 ? undefined : () => setStep(step - 1)
        }
        onNextPress={step === 4 ? undefined : () => {
          if (step === 1 && !isCoverPhotoExist) {
            setMissingCoverPhotoError(true);
          } else {
            setMissingCoverPhotoError(false);
            setStep(step + 1);
          }
        }}
        firstBookId={firstBookId}
      />

      {missingCoverPhotoError && (
        <Banner
          bg={'pinks.1'}
          color={'pinks.5'}
          heading={'Please add a cover photo to continue'}
          text={'You can change it anytime later.'}
          p="16px"
          size={'small'}
          mt={2}
        />
      )}

      <Box mb={step === 4 ? 0 : '100px'} p={(isValidating && step === 4) ? 0 : 2} width="100%">
        {renderStep()}
      </Box>
    </Flex>
  );
};

export default BookCreationForm;